import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { CalendarDays, Loader2, Search, MoreVertical, CheckCircle, XCircle, Clock, Ban, CreditCard, Banknote } from "lucide-react";
import { BookingsGroupedView } from "@/components/BookingsGroupedView";

const statusColors = {
  CONFIRMED: "bg-green-100 text-green-800", COMPLETED: "bg-green-100 text-green-800",
  PENDING_CASH: "bg-amber-100 text-amber-800", PENDING_PAYMENT: "bg-amber-100 text-amber-800",
  CANCELLED: "bg-red-100 text-red-800", EXPIRED: "bg-slate-100 text-slate-600",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const fetchBookings = async () => {
    try {
      const params = {};
      if (query) params.q = query;
      const res = await api.get("/admin/bookings", { params });
      setBookings(res.data);
    } catch { setBookings([]); }
    setLoading(false);
  };

  useEffect(() => { setLoading(true); fetchBookings(); }, []); // eslint-disable-line

  const handleSearch = () => { setLoading(true); fetchBookings(); };

  const overrideStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/admin/bookings/${bookingId}/status`, { status: newStatus });
      toast.success(`Booking status set to ${newStatus}`);
      fetchBookings();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const renderBooking = (b) => (
    <div className="bg-white border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-booking-${b.booking_id}`}>
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono text-muted-foreground">{b.booking_id?.slice(0, 16)}</span>
          <Badge className={`${statusColors[b.status] || ''} text-[10px] font-heading uppercase`}>{b.status?.replace("_", " ")}</Badge>
          <Badge variant="outline" className="text-[9px]">{b.payment_mode}</Badge>
        </div>
        <p className="text-sm font-medium">{b.user_name} <span className="text-muted-foreground text-xs">({b.user_email})</span></p>
        <p className="text-xs text-muted-foreground">{b.venue_name} · <span className="capitalize">{b.resource_name} ({b.sport_type})</span></p>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" />{b.start_dt?.slice(0, 10)}</span>
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{b.start_dt?.slice(11, 16)} - {b.end_dt?.slice(11, 16)}</span>
          <span className="flex items-center gap-1">{b.payment_mode === "ONLINE" ? <CreditCard className="w-3 h-3" /> : <Banknote className="w-3 h-3" />}{b.payment_mode}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <p className="font-heading text-lg font-bold text-court-orange">₱{b.total_amount?.toFixed(0)}</p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid={`override-booking-${b.booking_id}`} variant="ghost" size="sm" className="h-7 w-7 p-0">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => overrideStatus(b.booking_id, "CONFIRMED")} disabled={b.status === "CONFIRMED"}>
              <CheckCircle className="w-4 h-4 mr-2 text-green-600" />Force Confirm
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => overrideStatus(b.booking_id, "COMPLETED")} disabled={b.status === "COMPLETED"}>
              <Clock className="w-4 h-4 mr-2 text-blue-600" />Mark Complete
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => overrideStatus(b.booking_id, "CANCELLED")} disabled={b.status === "CANCELLED"} className="text-red-600">
              <XCircle className="w-4 h-4 mr-2" />Force Cancel
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => overrideStatus(b.booking_id, "EXPIRED")} disabled={b.status === "EXPIRED"}>
              <Ban className="w-4 h-4 mr-2 text-slate-500" />Mark Expired
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="admin-bookings-page">
      <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3 mb-1">
        <CalendarDays className="w-8 h-8 text-court-orange" />All Bookings
      </h1>
      <p className="text-muted-foreground mb-6">Platform-wide booking management</p>

      {/* Search */}
      <div className="flex gap-2 items-center mb-4 max-w-lg">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search by venue, user, or booking ID..." className="pl-9" data-testid="booking-search-input" />
        </div>
        <Button data-testid="booking-search-btn" onClick={handleSearch} className="bg-court-orange hover:bg-court-orange-hover text-white rounded-md">
          <Search className="w-4 h-4 mr-2" />Apply
        </Button>
      </div>

      <BookingsGroupedView
        bookings={bookings}
        renderItem={renderBooking}
        waitlistCount={0}
        waitlistItems={[]}
      />
    </div>
  );
}
